import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { copy, type Language } from '../content';
import { Icon } from './Icon';
import { whatsAppLink } from '../services/whatsapp';

type Message = { from: 'bot' | 'user'; text: string };
type Topic = { q: string; a: string; keywords: string[] };

/** Builds the answer set from the site copy so the bot stays in sync with the FAQ and pricing. */
function useTopics(language: Language): Topic[] {
  return useMemo(() => {
    const c = copy[language];
    const priceLine = c.pricing.plans
      .filter((plan) => plan.price)
      .map((plan) => `${plan.name}: ${plan.price!.monthly.toLocaleString('en-US')} ${c.pricing.currency}/${c.pricing.perMonth}`)
      .join(' — ');
    const pricing: Topic = {
      q: language === 'ar' ? 'كم أسعار الخطط؟' : 'How much are the plans?',
      a:
        `${priceLine}. ` +
        (language === 'ar'
          ? 'والمؤسسات بسعر مخصّص حسب الحجم. اضغط «اطلب عرضاً» لعرض رسمي.'
          : 'Enterprise is custom-priced. Use “Request a quote” for a formal offer.'),
      keywords: ['سعر', 'اسعار', 'أسعار', 'كام', 'تكلفة', 'خطة', 'خطط', 'price', 'cost', 'plan', 'how much'],
    };
    const fromFaq = c.faq.items.map((item) => ({
      q: item.question,
      a: item.answer,
      keywords: item.question.split(/\s+/).filter((word) => word.length > 3),
    }));
    return [pricing, ...fromFaq];
  }, [language]);
}

export function SupportChat({ language }: { language: Language }) {
  const isArabic = language === 'ar';
  const topics = useTopics(language);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { from: 'bot', text: isArabic ? 'أهلًا 👋 اسأل عن الأسعار أو أي شيء عن النظام، أو اختر سؤالًا:' : 'Hi 👋 Ask about pricing or anything about the system, or pick a question:' },
  ]);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const answerFor = (text: string): string => {
    const normalized = text.trim().toLowerCase();
    const hit = topics.find((topic) =>
      topic.q.toLowerCase() === normalized || topic.keywords.some((word) => normalized.includes(word.toLowerCase())),
    );
    if (hit) return hit.a;
    return isArabic
      ? 'سؤال جيد — للإجابة الدقيقة تواصل مع فريقنا مباشرة على واتساب بالأسفل.'
      : 'Good question — for a precise answer, reach our team directly on WhatsApp below.';
  };

  const send = (text: string) => {
    if (!text.trim()) return;
    setMessages((prev) => [...prev, { from: 'user', text }, { from: 'bot', text: answerFor(text) }]);
    setDraft('');
    window.requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }));
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    send(draft);
  };

  return (
    <div className={`support-chat ${open ? 'is-open' : ''}`}>
      {open && (
        <div className="support-chat__panel" role="dialog" aria-label={isArabic ? 'مساعد الرد السريع' : 'Quick support'}>
          <header className="support-chat__head">
            <span><i className="support-chat__dot" />{isArabic ? 'الرد السريع' : 'Quick support'}</span>
            <button type="button" onClick={() => setOpen(false)} aria-label={isArabic ? 'إغلاق' : 'Close'}><Icon name="close" /></button>
          </header>
          <div className="support-chat__body" ref={scrollRef}>
            {messages.map((message, index) => (
              <p className={`support-chat__msg support-chat__msg--${message.from}`} key={index}>{message.text}</p>
            ))}
            <div className="support-chat__chips">
              {topics.slice(0, 4).map((topic) => (
                <button type="button" key={topic.q} onClick={() => send(topic.q)}>{topic.q}</button>
              ))}
            </div>
          </div>
          <form className="support-chat__input" onSubmit={onSubmit}>
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={isArabic ? 'اكتب سؤالك…' : 'Type your question…'}
              aria-label={isArabic ? 'سؤالك' : 'Your question'}
            />
            <button type="submit" aria-label={isArabic ? 'إرسال' : 'Send'}><Icon name="arrow" /></button>
          </form>
          <a className="support-chat__wa" href={whatsAppLink()} target="_blank" rel="noreferrer noopener">
            <Icon name="chat" />{isArabic ? 'تحدث مع فريقنا على واتساب' : 'Talk to our team on WhatsApp'}
          </a>
          <Link className="support-chat__quote" to="/contact" onClick={() => setOpen(false)}>
            {isArabic ? 'أو اطلب عرض سعر' : 'Or request a quote'}<Icon name="arrow" />
          </Link>
        </div>
      )}
      <button
        className="support-chat__toggle"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={isArabic ? 'الرد السريع' : 'Quick support'}
      >
        <Icon name={open ? 'close' : 'chat'} />
        {!open && <span>{isArabic ? 'مساعدة سريعة' : 'Quick help'}</span>}
      </button>
    </div>
  );
}
